/**
 * RequestsNotificationContext - Provides notification counts for both:
 * - Creators: pending requests count
 * - Guests: unseen status changes count
 *
 * Badge clearing: When a user visits the requests page, clearRequestsBadge()
 * stores the current raw API count as a watermark. The badge only re-appears
 * when new items arrive (count exceeds watermark). The watermark resets when
 * all items are resolved (count drops to 0).
 */
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { API_URL } from '../config';
import { getAuthHeaders } from '../services/api';

interface RequestsNotificationContextType {
  // For creators: count of pending requests to their shoots
  pendingCount: number;
  refreshPendingCount: () => Promise<void>;
  // For guests: count of booking requests with unseen status changes
  unseenStatusCount: number;
  refreshUnseenStatusCount: () => Promise<void>;
  markRequestsSeen: () => Promise<void>;
  // Clear badges when user visits the requests page
  clearRequestsBadge: () => void;
}

const RequestsNotificationContext = createContext<RequestsNotificationContextType>({
  pendingCount: 0,
  refreshPendingCount: async () => {},
  unseenStatusCount: 0,
  refreshUnseenStatusCount: async () => {},
  markRequestsSeen: async () => {},
  clearRequestsBadge: () => {},
});

export const useRequestsNotifications = () => useContext(RequestsNotificationContext);

interface RequestsNotificationProviderProps {
  children: React.ReactNode;
  userId: string | number | undefined;
  userRole?: string;
}

export const RequestsNotificationProvider: React.FC<RequestsNotificationProviderProps> = ({
  children,
  userId,
  userRole
}) => {
  const [pendingCount, setPendingCount] = useState(0);
  const [unseenStatusCount, setUnseenStatusCount] = useState(0);
  const isMountedRef = useRef(true);

  // Raw API counts (always reflect latest server value, unaffected by clearing)
  const rawPendingRef = useRef(0);
  const rawUnseenRef = useRef(0);
  // Watermarks: badge suppressed until count exceeds these values
  const clearedPendingRef = useRef(0);
  const clearedUnseenRef = useRef(0);
  // Deferred clear: set when clearBadge fires before initial fetch completes
  // Separate flags for each fetch since they run concurrently
  const pendingClearPendingRef = useRef(false);
  const pendingClearUnseenRef = useRef(false);

  // Reset all watermark refs when userId changes (prevents cross-user badge suppression)
  useEffect(() => {
    rawPendingRef.current = 0;
    rawUnseenRef.current = 0;
    clearedPendingRef.current = 0;
    clearedUnseenRef.current = 0;
    pendingClearPendingRef.current = false;
    pendingClearUnseenRef.current = false;
  }, [userId]);

  // Fetch pending requests count for authenticated users
  const refreshPendingCount = useCallback(async () => {
    if (!userId) {
      setPendingCount(0);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/requests/pending-count`, {
        headers: { ...getAuthHeaders() },
      });

      if (response.ok) {
        const data = await response.json();
        const newCount = data.count || 0;
        rawPendingRef.current = newCount;

        if (isMountedRef.current) {
          // Handle deferred clear: clearBadge was called before this fetch returned
          if (pendingClearPendingRef.current) {
            pendingClearPendingRef.current = false;
            if (newCount > clearedPendingRef.current) {
              clearedPendingRef.current = newCount;
            }
            setPendingCount(0);
          } else if (clearedPendingRef.current > 0) {
            if (newCount === 0) {
              // All items resolved — reset watermark
              setPendingCount(0);
              clearedPendingRef.current = 0;
            } else if (newCount > clearedPendingRef.current) {
              // New items beyond watermark — show only the delta
              setPendingCount(newCount - clearedPendingRef.current);
            } else {
              // Still suppressed
              setPendingCount(0);
            }
          } else {
            setPendingCount(newCount);
          }
        }
      }
    } catch (error) {
      // Reset deferred clear flag on failure to prevent stuck suppression
      pendingClearPendingRef.current = false;
      console.error('📋 [NOTIFICATIONS] Error fetching pending count:', error);
    }
  }, [userId]);

  // Fetch unseen status count for guests
  const refreshUnseenStatusCount = useCallback(async () => {
    if (!userId) {
      setUnseenStatusCount(0);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/requests/unseen-status-count`, {
        headers: { ...getAuthHeaders() },
      });

      if (response.ok) {
        const data = await response.json();
        const newCount = data.count || 0;
        rawUnseenRef.current = newCount;

        if (isMountedRef.current) {
          // Handle deferred clear
          if (pendingClearUnseenRef.current) {
            pendingClearUnseenRef.current = false;
            if (newCount > clearedUnseenRef.current) {
              clearedUnseenRef.current = newCount;
            }
            setUnseenStatusCount(0);
          } else if (clearedUnseenRef.current > 0) {
            if (newCount === 0) {
              setUnseenStatusCount(0);
              clearedUnseenRef.current = 0;
            } else if (newCount > clearedUnseenRef.current) {
              // New items beyond watermark — show only the delta
              setUnseenStatusCount(newCount - clearedUnseenRef.current);
            } else {
              setUnseenStatusCount(0);
            }
          } else {
            setUnseenStatusCount(newCount);
          }
        }
      }
    } catch (error) {
      // Reset deferred clear flag on failure to prevent stuck suppression
      pendingClearUnseenRef.current = false;
      console.error('📋 [NOTIFICATIONS] Error fetching unseen status count:', error);
    }
  }, [userId]);

  // Mark all requests as seen (called when viewing My Requests page)
  const markRequestsSeen = useCallback(async () => {
    if (!userId) return;

    try {
      await fetch(`${API_URL}/api/requests/mark-seen`, {
        method: 'POST',
        headers: { ...getAuthHeaders() },
      });

      if (isMountedRef.current) {
        setUnseenStatusCount(0);
        // Server reset the unseen count, so clear watermark too
        clearedUnseenRef.current = 0;
        rawUnseenRef.current = 0;
      }
    } catch (error) {
      console.error('📋 [NOTIFICATIONS] Error marking requests seen:', error);
    }
  }, [userId]);

  // Clear badges when user visits the requests page.
  // Reads from raw refs (not stale state) so it works correctly:
  // - On first mount (before polls return)
  // - On rapid re-visits
  // - After polls have updated the count
  const clearRequestsBadge = useCallback(() => {
    const rawPending = rawPendingRef.current;
    const rawUnseen = rawUnseenRef.current;

    // Only raise the watermark, never lower it (prevents rapid re-visit bug)
    if (rawPending > clearedPendingRef.current) {
      clearedPendingRef.current = rawPending;
    }
    if (rawUnseen > clearedUnseenRef.current) {
      clearedUnseenRef.current = rawUnseen;
    }

    // If raws are still 0 (initial fetch hasn't returned), defer the clear
    // so the first fetch result will set the watermark correctly
    if (rawPending === 0 && clearedPendingRef.current === 0) {
      pendingClearPendingRef.current = true;
    }
    if (rawUnseen === 0 && clearedUnseenRef.current === 0) {
      pendingClearUnseenRef.current = true;
    }

    setPendingCount(0);
    setUnseenStatusCount(0);
  }, []); // Stable identity — reads from refs only

  // Store callbacks in refs to avoid dependency issues
  const refreshPendingCountRef = useRef(refreshPendingCount);
  const refreshUnseenStatusCountRef = useRef(refreshUnseenStatusCount);

  useEffect(() => {
    refreshPendingCountRef.current = refreshPendingCount;
    refreshUnseenStatusCountRef.current = refreshUnseenStatusCount;
  }, [refreshPendingCount, refreshUnseenStatusCount]);

  // Poll for counts periodically (optimized with visibility API)
  useEffect(() => {
    if (!userId) {
      setPendingCount(0);
      setUnseenStatusCount(0);
      return;
    }

    let pollInterval: NodeJS.Timeout | null = null;
    let isMounted = true;

    const fetchBothCounts = () => {
      if (!isMounted) return;
      refreshPendingCountRef.current();
      refreshUnseenStatusCountRef.current();
    };

    const startPolling = () => {
      if (pollInterval) return;
      pollInterval = setInterval(fetchBothCounts, 30000);
    };

    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchBothCounts();
        startPolling();
      } else {
        stopPolling();
      }
    };

    // Initial fetch
    fetchBothCounts();

    if (document.visibilityState === 'visible') {
      startPolling();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMounted = false;
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userId]);

  // Cleanup
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <RequestsNotificationContext.Provider value={{
      pendingCount,
      refreshPendingCount,
      unseenStatusCount,
      refreshUnseenStatusCount,
      markRequestsSeen,
      clearRequestsBadge
    }}>
      {children}
    </RequestsNotificationContext.Provider>
  );
};

export default RequestsNotificationContext;
